import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Spin,
} from 'antd';
import FormLegend from 'components/Forms/FormLegend';
import AddAssetModal from 'components/Modals/AddAssetModal';
import WarrantiesTable from 'components/Warranties/WarrantiesTable';
import WorkOrderDetailsContext from 'containers/WorkOrder/WorkOrderDetailsContext';
import _, { capitalize, isArray } from 'lodash';
import { observe, reaction } from 'mobx';
import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';
import { customFleets } from 'shared';
import { isUfpFleet } from 'utils/customFleetHelpers';
import { AppStore, ContactStore, ShopStore, WorkOrderStore } from 'stores';
import CarStore from 'stores/CarStore';
import { Logger } from 'stores/Classes';
import { WorkOrderTypeOfServiceEnum } from 'stores/Classes/WorkOrderObject';
import CurrentUserStore from 'stores/CurrentUserStore';
import WarrantyStore from 'stores/WarrantyStore';
import { Link } from 'react-router-dom';
import {
  calcMileageBack,
  convertHourToSeconds,
} from 'helpers/unitCalculations';

const { Option } = Select;

const fullWidth = {
  width: '100%',
};

const WorkOrderDetailsForm = ({
  onFormDataChange,
  childRef,
  handleWODetailsStateChange,
}) => {
  const [form] = Form.useForm();
  const context = useContext(WorkOrderDetailsContext);

  const initialRenderRef = useRef(true);

  const [state, setState] = useState({
    cars: [],
    locations: [],
    allCars: [],
    loadingShopWorkOrderStatuses: true,
    shopWorkOrderStatuses: [],
    loadingCarData: false,
    selectedCar: undefined,
    selectedCarFromForm: undefined,
    searchingCar: false,
    assetTypeDataSource: ['Vehicle', 'Trailer', 'Other'],
    addAssetModalVisible: false,
    loadingAssetWarranties: false,
    warrantyFilters: {},
    warrantyPagination: {},
    warrantySorter: {},
    assetWarranties: [],
    vendors: [],
    loadingVendors: false,
    woStatusValue: null,
    preSelectedAssets: [],
    repairTypes: [],
    isShopDataReady: false,
  });

  const [isFleetUfp, setIsFleetUfp] = useState(() => isUfpFleet());

  useEffect(() => {
    if (childRef) {
      childRef(form);
    }
  }, [childRef]);

  useEffect(() => {
    const isInitialRender = initialRenderRef.current;

    if (isInitialRender) {
      initialRenderRef.current = false;

      const init = async () => {
        await initAssets();
        await loadShopWorkOrderStatuses();
        await loadShopRepairTypes();
        await loadShopLocations();
        await loadShopVendors();

        setState((prev) => ({ ...prev, isShopDataReady: true }));
      };

      init();

      if (context.state.initialFormValues?.woSelectVehicle) {
        handleInitialVehicle();
      }

      const shopChangeWatcher = observe(ShopStore.currentShop, 'id', () => {
        if (ShopStore.isAdmin()) return;
        return init();
      });

      return () => {
        shopChangeWatcher();
      };
    }
  }, []);

  useEffect(() => {
    handleInitialVehicle();
  }, [state.allCars]);

  useEffect(() => {
    if (!initialRenderRef.current && onFormDataChange) {
      const initialValues = {
        woInvoiceNumber:
          context.state.initialFormValues.wOid ||
          context.state.initialFormValues.woInvoiceNumber,
        woAssetType: context.state.initialFormValues.woAssetType || 'Vehicle',
        woSelectVehicle: context.state.initialFormValues.woSelectVehicle,
        woStatus: context.state.initialFormValues.woStatus || '',
        woRepairType: context.state.initialFormValues.woRepairType,
        woCreatedBy:
          context.state.initialFormValues.woCreatedBy || getCurrentLoggedUser(),
        woAssignedTo: context.state.initialFormValues.woAssignedTo,
        invoiceNumberCustomer:
          context.state.initialFormValues.invoiceNumberCustomer,
        additionalNotes: context.state.initialFormValues.additionalNotes,
      };

      onFormDataChange(initialValues, true);
    }
  }, [initialRenderRef.current]);

  useEffect(() => {
    setIsFleetUfp(isUfpFleet());

    const disposer = reaction(
      () => ShopStore.currentShop.id,
      () => setIsFleetUfp(isUfpFleet())
    );
    return () => disposer();
  }, []);

  useEffect(() => {
    handleWODetailsStateChange({
      loadingCarData: state.loadingCarData,
      isShopDataReady: state.isShopDataReady,
    });
  }, [state.loadingCarData, state.isShopDataReady, handleWODetailsStateChange]);

  const handleInitialVehicle = async () => {
    setState((prev) => ({ ...prev, searchingCar: true, loadingCarData: true }));
    const car = state.allCars.find(
      (car) => car.id === context.state.initialFormValues.woSelectVehicle
    );
    context.onGetCarDetails(car);

    if (car) {
      await form.setFieldValue('woAssetType', _.capitalize(car.carType));
    }

    setState((prev) => ({
      ...prev,
      selectedCar: context.state.initialFormValues.woSelectVehicle,
      searchingCar: false,
      loadingCarData: false,
    }));

    onChangeFormCar(context.state.initialFormValues.woSelectVehicle);
  };

  const loadShopVendors = async () => {
    setState((prev) => ({ ...prev, loadingVendors: true }));
    try {
      if (
        !ShopStore.currentShop.id ||
        ShopStore.currentShop.id === '-1' ||
        ShopStore.currentShop.id === -1
      ) {
        setState((prev) => ({ ...prev, vendors: [], loadingVendors: false }));
        return;
      }
      const vendors = await ContactStore.getShopContacts(
        ShopStore.currentShop.id
      );
      setState((prev) => ({
        ...prev,
        vendors: vendors || [],
        loadingVendors: false,
      }));
    } catch (error) {
      Logger.error(error);
      setState((prev) => ({ ...prev, loadingVendors: false }));
    }
  };

  const loadShopLocations = async () => {
    try {
      if (
        !ShopStore.currentShop.id ||
        ShopStore.currentShop.id === '-1' ||
        ShopStore.currentShop.id === -1
      ) {
        setState((prev) => ({ ...prev, locations: [] }));
        return;
      }
      const locations = await ShopStore.getShopLocations(
        ShopStore.currentShop.id
      );
      setState((prev) => ({ ...prev, locations: locations || [] }));
    } catch (error) {
      Logger.error(error);
    }
  };

  const loadAssetWarranties = async (carId) => {
    try {
      setState((prev) => ({
        ...prev,
        loadingAssetWarranties: true,
        assetWarranties: [],
      }));

      const filters = {
        ...state.warrantyFilters,
        carIds: carId,
      };

      if (filters.status?.length) {
        filters.statuses = filters.status.join(',');
      }

      if (state.warrantyPagination.current) {
        const page = state.warrantyPagination.current;
        filters.limit = state.warrantyPagination.pageSize;
        filters.offset = (page - 1) * filters.limit;
      }

      if (state.warrantySorter.column && state.warrantySorter.order) {
        const field = state.warrantySorter.field;
        const direction = state.warrantySorter.order === 'ascend' ? '' : '-';
        filters.sortBy = `${direction}${field}`;
      }

      const { data, meta } = await WarrantyStore.getWarranties(
        ShopStore.currentShop.id,
        filters
      );

      setState((prev) => ({
        ...prev,
        assetWarranties: data || [],
        warrantyPagination: {
          ...prev.warrantyPagination,
          total: meta?.total || 0,
        },
        loadingAssetWarranties: false,
      }));
    } catch (error) {
      Logger.error(error.toString());
      setState((prev) => ({ ...prev, loadingAssetWarranties: false }));
    }
  };

  const getCreatedByOptions = () => {
    return context.shopUsers.map((user) => (
      <Option key={user.id} value={user.id}>
        {user.name}
      </Option>
    ));
  };

  const getAssignedToOptions = () => {
    return context.shopUsers.map((user) => (
      <Option key={user.id} value={user.id}>
        {user.name}
      </Option>
    ));
  };

  const getAssetOptions = () => {
    const assetType = form.getFieldValue('woAssetType');

    // If no asset type is selected, default to showing vehicles
    const filterType = assetType || 'Vehicle';

    const filteredCarsBasedOnAssetType = state.cars.filter(
      (car) => (car?.carType || '').toLowerCase() === filterType.toLowerCase()
    );

    return filteredCarsBasedOnAssetType.map((car, index) => {
      const vinInfo = car?.carType !== 'other' ? ` - ${car?.vin}` : '';
      return (
        <Option key={`${car?.id}-${index}`} value={car?.id}>
          {`${car?.carName || car?.unitId || car?.make + ' ' + car?.model + ' ' + car?.year}${vinInfo}`}
        </Option>
      );
    });
  };

  const initAssets = async () => {
    setState((prev) => ({ ...prev, searchingCar: true, loadingCarData: true }));
    const assets = await getShopCars();

    setState((prev) => ({
      ...prev,
      allCars: assets && assets.length ? [...assets] : [],
      cars: assets && assets.length ? [...assets] : [],
      selectedCar: undefined,
      searchingCar: false,
      loadingCarData: false,
    }));
  };

  const loadShopWorkOrderStatuses = async () => {
    try {
      setState((prev) => ({ ...prev, loadingShopWorkOrderStatuses: true }));
      const shopWorkOrderStatuses =
        await WorkOrderStore.getShopWorkOrderStatuses(ShopStore.currentShop.id);
      setState((prev) => ({
        ...prev,
        shopWorkOrderStatuses: _.chain(shopWorkOrderStatuses)
          .get('data', [])
          .map((status) => ({
            id: status.id,
            key: status.key,
            name: status.description,
            description: status.description,
            color: status.color,
            text: status.description,
            value: status.key,
            is_default: status.is_default,
          }))
          .value(),
      }));
    } catch (error) {
      Logger.error(error);
      AppStore.addError('Error loading work order statuses');
    } finally {
      setState((prev) => ({ ...prev, loadingShopWorkOrderStatuses: false }));
    }
  };

  const loadShopRepairTypes = async () => {
    try {
      const { data: repairTypes } = await ShopStore.getShopRepairTypes(
        ShopStore.currentShop.id
      );

      if (repairTypes.length) {
        const newRepairTypes = repairTypes.map((type) => {
          return {
            key: type.value,
            value: type.title,
          };
        });
        setState((prev) => ({ ...prev, repairTypes: newRepairTypes || [] }));
      }
    } catch (error) {
      Logger.error(error);
      AppStore.addError('Error loading work order statuses');
    }
  };

  const getShopCars = async () => {
    return await CarStore.getShopCarsBasicInfoAndReturn(
      ShopStore.currentShop.id
    );
  };

  const searchForCars = async (search) => {
    setState((prev) => ({ ...prev, searchingCar: true }));
    const cars = await state.allCars;
    const filteredCars = cars.filter((car) => {
      return (
        (car?.carName || '').toLowerCase().includes(search.toLowerCase()) ||
        (car?.unitId || '').toLowerCase().includes(search.toLowerCase()) ||
        (car?.vin || '').toLowerCase().includes(search.toLowerCase())
      );
    });
    setState((prev) => ({ ...prev, cars: filteredCars, searchingCar: false }));
  };

  const onCarSearch = async (value) => {
    if (!value) {
      return setState((prev) => ({ ...prev, cars: state.allCars }));
    }
    searchForCars(value);
  };

  const onCarSearchChange = (selectedCar) => {
    setState((prev) => ({ ...prev, selectedCar }));
    context.onCarSelect(selectedCar);
    const car = state.allCars.find((car) => car.id === selectedCar);

    if (state.isShopDataReady && isFleetUfp) {
      const location = car.tags?.[0]?.name;

      if (location) {
        const [_, city, privince] = location.split('-');
        const carLocation = state.locations.find((location) =>
          location.description.includes(`${city}, ${privince}`)
        );

        if (carLocation) {
          const formattedValue = `${carLocation.id}-${carLocation.code}-${carLocation.description}`;
          const code = carLocation.code;

          const currentWOInvoiceNumber = form.getFieldValue('woInvoiceNumber');
          const currentWOInvoiceWithoutLocation =
            currentWOInvoiceNumber.indexOf('-') > -1
              ? currentWOInvoiceNumber.split('-')[1]
              : currentWOInvoiceNumber;

          const newWOInvoiceNumber = `${code}-${currentWOInvoiceWithoutLocation}`;

          form.setFieldsValue({
            locationCode: formattedValue,
            woInvoiceNumber: newWOInvoiceNumber,
          });

          context.onLocationSelect(formattedValue);
          onFieldChange({ locationCode: formattedValue });
        }
      }
    }

    context.onGetCarDetails(car);
  };

  const onChangeFormCar = (value) => {
    setState((prev) => ({
      ...prev,
      selectedCarFromForm: value,
      preSelectedAssets: [value],
    }));

    if (value) {
      loadAssetWarranties(value);
    }

    context.onCarSelect(value);
    const car = state.allCars.find((car) => car.id === value);
    context.onGetCarDetails(car);
  };

  const onChangeAssignedTo = (value) => {
    context.onChangeAssignedTo(value);
  };

  const getCurrentLoggedUser = () => {
    return CurrentUserStore.user.id;
  };

  const onAddAssets = async (newAssets) => {
    setState((prev) => ({ ...prev, addAssetModalVisible: false }));

    if (newAssets.length) {
      try {
        let firstAssetAddedId = null;
        for (const asset of newAssets) {
          const mileageInKm = calcMileageBack(asset.mileage);
          const engineHoursInSeconds = convertHourToSeconds(asset.engineHours);

          const response = await CarStore.createGenericAsset({
            vin: asset.vin,
            source: 'user',
            assetType: asset.assetType,
            shopId: ShopStore.currentShop.id,
            licensePlate: '',
            mileageInKm: mileageInKm || 0,
            unitId: asset.unitId,
            engineHoursInSecond: engineHoursInSeconds || 0,
            make: asset.make,
            model: asset.model,
            year: asset.year,
            location: asset.location,
            mainDriver: asset.mainDriver,
          });
          if (!firstAssetAddedId) {
            firstAssetAddedId = isArray(response)
              ? response[0].id
              : response.id;
          }
        }
        AppStore.addSuccess('Asset added successfully');
        await initAssets();

        await form.setFieldsValue({
          woAssetType: capitalize(newAssets[0].assetType),
          woSelectVehicle: firstAssetAddedId,
        });
      } catch {
        AppStore.addError('Error adding assets');
      }
    }
  };

  const filterOption = (input, option) => {
    return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  const rawStatusOptions = state.shopWorkOrderStatuses;
  const defaultStatus = rawStatusOptions.find((status) => status.is_default) ||
    rawStatusOptions[0] || { key: 'open', value: 'Open' };

  const rawTypeOptions = Object.keys(WorkOrderTypeOfServiceEnum)
    .filter((type) => {
      return [
        'driver_identified',
        'breakdown',
        'preventive_maintenance',
        'inspection',
      ].includes(type);
    })
    .map((woRepairPriorityKey) => ({
      key: woRepairPriorityKey,
      value: WorkOrderTypeOfServiceEnum[woRepairPriorityKey],
    }));

  if (state.loadingCarData || !state.isShopDataReady) {
    return <Spin />;
  }

  const onFieldChange = (changedValues, allValues) => {
    if (onFormDataChange && !initialRenderRef.current) {
      onFormDataChange(allValues);
    }
    if (context.handleFormChange && !initialRenderRef.current) {
      context.handleFormChange();
    }
  };

  return (
    <Form
      form={form}
      className="limit-size-tablets"
      initialValues={{
        woInvoiceNumber:
          context.state.initialFormValues.wOid ||
          context.state.initialFormValues.woInvoiceNumber,
        woAssetType: context.state.initialFormValues.woAssetType || 'Vehicle',
        woSelectVehicle: context.state.initialFormValues.woSelectVehicle,
        woStatus: context.state.initialFormValues.woStatus || defaultStatus.key,
        woRepairType: context.state.initialFormValues.woRepairType,
        woCreatedBy:
          context.state.initialFormValues.woCreatedBy || getCurrentLoggedUser(),
        woAssignedTo: context.state.initialFormValues.woAssignedTo,
        locationCode: context.state.initialFormValues.locationCode,
        invoiceNumberCustomer:
          context.state.initialFormValues.invoiceNumberCustomer,
        additionalNotes: context.state.initialFormValues.additionalNotes,
        vendor: context.state.initialFormValues.vendor_id,
      }}
      onValuesChange={onFieldChange}
      layout="vertical"
    >
      <FormLegend>Initial Details</FormLegend>

      {!isFleetUfp ? (
        <Row gutter={[16]}>
          <Col span={8}>
            <Form.Item
              label="WO Number:"
              name="woInvoiceNumber"
              rules={[
                { required: true, message: 'Work order number is required!' },
              ]}
            >
              <Input
                prefix="#"
                allowClear
                placeholder="#000000"
                disabled={context.state.isDisabled}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Asset Type:" name="woAssetType">
              <Select
                style={fullWidth}
                placeholder="Asset Type"
                disabled={context.state.isDisabled}
                onChange={(value) => {
                  onFieldChange({ woAssetType: value });
                  form.setFieldValue('woSelectVehicle', null);
                }}
              >
                <Option value="Vehicle">Vehicle</Option>
                <Option value="Trailer">Trailer</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            {state.loadingCarData ? (
              <Spin />
            ) : (
              <>
                <Form.Item
                  label="Select Asset:"
                  name="woSelectVehicle"
                  rules={[{ required: true, message: 'Asset is required!' }]}
                  style={{ marginBottom: 3 }}
                >
                  <Select
                    style={fullWidth}
                    onChange={onCarSearchChange}
                    onSearch={onCarSearch}
                    placeholder="Search by Unit ID"
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    loading={state.searchingCar}
                    disabled={context.state.isDisabled}
                  >
                    {getAssetOptions()}
                  </Select>
                </Form.Item>
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      addAssetModalVisible: true,
                    }))
                  }
                >
                  Add Asset
                </Button>
              </>
            )}
          </Col>
          <Col span={12}>
            <Form.Item
              label="WO Status:"
              name="woStatus"
              rules={[{ required: true, message: 'Status is required!' }]}
            >
              <Radio.Group
                className="stacked-by-two"
                style={fullWidth}
                disabled={
                  context.state.isDisabled || state.loadingShopWorkOrderStatuses
                }
              >
                {rawStatusOptions.map((status) => (
                  <Radio
                    className="stack-child"
                    key={status.key}
                    value={status.key}
                  >
                    {status.name}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Repair Type:"
              name="woRepairType"
              rules={[{ required: true, message: 'Repair Type is required!' }]}
            >
              <Radio.Group className="stacked-by-two" style={fullWidth}>
                {(state.repairTypes.length
                  ? state.repairTypes
                  : rawTypeOptions
                ).map((type) => (
                  <Radio
                    className="stack-child"
                    key={type.key}
                    value={type.key}
                  >
                    {type.value}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            {context.state.inspectionFormId && (
              <div>
                View inspection form:{' '}
                <Link
                  to={`/inspection-forms/forms/${context.state.inspectionFormId}/edit`}
                >
                  {context.state.inspectionFormTitle}
                </Link>
              </div>
            )}
          </Col>
          <Col span={8}>
            <Form.Item
              label="Created by:"
              name="woCreatedBy"
              rules={[{ required: true, message: 'Created by is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a User"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={context.state.isDisabled}
              >
                {getCreatedByOptions()}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Assigned To:"
              name="woAssignedTo"
              rules={[{ required: true, message: 'Assigned to is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a User"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={context.state.isDisabled}
                onChange={onChangeAssignedTo}
              >
                {getAssignedToOptions()}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      ) : (
        // UFP Fleet form content
        <Row gutter={[16]}>
          <Col span={8}>
            <Form.Item
              label="WO Number:"
              name="woInvoiceNumber"
              rules={[
                { required: true, message: 'Work order number is required!' },
              ]}
            >
              <Input
                prefix="#"
                allowClear
                placeholder="#000000"
                disabled={context.state.isDisabled}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Repair Type:"
              name="woRepairType"
              rules={[{ required: true, message: 'Repair Type is required!' }]}
              style={
                context.state.inspectionFormId ? { marginBottom: 0 } : undefined
              }
            >
              <Select
                style={fullWidth}
                placeholder="Select Repair Type"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                data-testid="wo-repair-type-select"
              >
                {(state.repairTypes.length
                  ? state.repairTypes
                  : rawTypeOptions
                ).map((type) => (
                  <Option key={type.key} value={type.key}>
                    {type.value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {context.state.inspectionFormId && (
              <div style={{ marginBottom: '5px' }}>
                View inspection form:{' '}
                <Link
                  to={`/inspection-forms/forms/${context.state.inspectionFormId}/edit`}
                >
                  {context.state.inspectionFormTitle}
                </Link>
              </div>
            )}
          </Col>
          <Col span={8}>
            <Form.Item
              label="WO Status:"
              name="woStatus"
              rules={[{ required: true, message: 'Status is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select Status"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={
                  context.state.isDisabled || state.loadingShopWorkOrderStatuses
                }
                data-testid="wo-status-select"
              >
                {rawStatusOptions.map((status) => (
                  <Option key={status.key} value={status.key}>
                    {status.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Created by:"
              name="woCreatedBy"
              rules={[{ required: true, message: 'Created by is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a User"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={context.state.isDisabled}
                data-testid="wo-created-by-select"
              >
                {getCreatedByOptions()}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Assigned To:"
              name="woAssignedTo"
              rules={[{ required: true, message: 'Assigned to is required!' }]}
            >
              <Select
                style={fullWidth}
                placeholder="Select a User"
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                disabled={context.state.isDisabled}
                onChange={onChangeAssignedTo}
                data-testid="wo-assigned-to-select"
              >
                {getAssignedToOptions()}
              </Select>
            </Form.Item>
          </Col>
          {/* Asset Type and Selection */}
          <Col span={8}>
            <Form.Item label="Asset Type:" name="woAssetType">
              <Select
                style={fullWidth}
                placeholder="Asset Type"
                data-testid="wo-asset-type-select"
                disabled={context.state.isDisabled}
                onChange={(value) => {
                  onFieldChange({ woAssetType: value });
                  form.setFieldValue('woSelectVehicle', null);
                }}
              >
                <Option value="Vehicle">Vehicle</Option>
                <Option value="Trailer">Trailer</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            {state.loadingCarData ? (
              <Spin />
            ) : (
              <>
                <Form.Item
                  label="Select Asset:"
                  name="woSelectVehicle"
                  rules={[{ required: true, message: 'Asset is required!' }]}
                  style={{ marginBottom: 3 }}
                >
                  <Select
                    style={fullWidth}
                    onChange={onCarSearchChange}
                    onSearch={onCarSearch}
                    placeholder="Search by Unit ID"
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    loading={state.searchingCar}
                    disabled={context.state.isDisabled}
                    data-testid="wo-select-asset-select"
                  >
                    {getAssetOptions()}
                  </Select>
                </Form.Item>
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      addAssetModalVisible: true,
                    }))
                  }
                >
                  Add Asset
                </Button>
              </>
            )}
          </Col>
          {/* Vendor Selection */}
          <Col xs={24} sm={8}>
            <Form.Item label="Vendor:" name="vendor">
              <Select
                style={fullWidth}
                value={context.state.initialFormValues?.vendor_id}
                onChange={(value) => {
                  context.setInitialFormValue('vendor_id', value);
                  // onFieldChange({ vendor_id: value });                     // <- looks like doesn't work, removes branch in store
                }}
                placeholder={
                  state.loadingVendors
                    ? 'Loading vendors...'
                    : 'Select a Vendor'
                }
                showSearch
                optionFilterProp="children"
                filterOption={filterOption}
                loading={state.loadingVendors}
                disabled={context.state.isDisabled || state.loadingVendors}
                notFoundContent={
                  state.loadingVendors ? 'Loading...' : 'No vendors found'
                }
                data-testid="wo-vendor-select"
              >
                {!state.loadingVendors &&
                  state.vendors.map((vendor) => (
                    <Option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          {/* Location Selection */}
          <Col xs={24} sm={12}>
            <Form.Item
              label="Attach a Location to this Work Order:"
              name="locationCode"
            >
              <Select
                style={fullWidth}
                placeholder="Select a Location"
                disabled={context.state.isDisabled}
                onChange={(formattedValue) => {
                  const code = formattedValue.split('-')[1];
                  const currentWOInvoiceNumber =
                    form.getFieldValue('woInvoiceNumber');
                  const currentWOInvoiceWithoutLocation =
                    currentWOInvoiceNumber.indexOf('-') > -1
                      ? currentWOInvoiceNumber.split('-')[1]
                      : currentWOInvoiceNumber;
                  const newWOInvoiceNumber = `${code}-${currentWOInvoiceWithoutLocation}`;

                  form.setFieldValue('woInvoiceNumber', newWOInvoiceNumber);
                  context.onLocationSelect(formattedValue);
                  onFieldChange({ locationCode: formattedValue });
                  // the context handleFormChange is used to update the values that will be sent to save the work order
                  context.handleFormChange({
                    woInvoiceNumber: newWOInvoiceNumber,
                  });
                }}
                filterOption={(input, option) => {
                  const lowerCaseInput = input.toLowerCase();
                  const optionValue = option.props.value.toLowerCase();
                  return optionValue.includes(lowerCaseInput);
                }}
                showSearch
                optionFilterProp="children"
                data-testid="wo-location-select"
              >
                {state.locations.map((location) => (
                  <Option
                    key={`${location.id}-${location.code}-${location.description}`}
                    value={`${location.id}-${location.code}-${location.description}`}
                  >
                    {location.code} - {location.description}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {/* Invoice Number Customer */}
          <Col xs={24} sm={isFleetUfp ? 12 : 24}>
            <Form.Item label="Invoice Number:" name="invoiceNumberCustomer">
              <Input
                prefix="#"
                allowClear
                placeholder="000000"
                disabled={context.state.isDisabled}
              />
            </Form.Item>
          </Col>
          {/* Additional Notes */}
          <Col xs={24}>
            <Form.Item label="Additional Notes:" name="additionalNotes">
              <Input.TextArea
                style={fullWidth}
                placeholder="Additional Notes"
                data-testid="wo-additional-notes-input"
                disabled={context.state.isDisabled}
                rows={6}
                onChange={(e) => {
                  context.setInitialFormValue(
                    'additionalNotes',
                    e.target.value
                  );
                  // onFieldChange({ additionalNotes: e.target.value });  // <- looks like doesn't work, removes branch in store
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* Warranties Section */}
      <Row gutter={16}>
        <Col span={24}>
          <div style={{ marginTop: 15, marginBottom: 15 }}>
            <FormLegend>Warranties on this Asset</FormLegend>
            <Collapse defaultActiveKey={[]}>
              <Collapse.Panel
                header={
                  <span data-testid="warranties-collapse-header-text">
                    {`(${state.assetWarranties.length}) warranties`}
                  </span>
                }
                key="1"
                collapsible={
                  state.loadingAssetWarranties ? 'disabled' : 'header'
                }
              >
                <WarrantiesTable
                  isLoading={state.loadingAssetWarranties}
                  data={state.assetWarranties}
                  assets={state.allCars}
                  vendors={state.vendors}
                  preSelectedAssets={state.preSelectedAssets}
                  onUpdate={() => {
                    loadAssetWarranties(state.selectedCarFromForm);
                  }}
                  onChangePagination={({ pagination, filters, sorter }) => {
                    setState((prev) => ({
                      ...prev,
                      warrantyPagination: pagination,
                      warrantyFilters: filters,
                      warrantySorter: sorter,
                    }));
                    loadAssetWarranties(state.selectedCarFromForm);
                  }}
                />
              </Collapse.Panel>
            </Collapse>
          </div>
        </Col>
      </Row>

      <AddAssetModal
        visible={state.addAssetModalVisible}
        hideVin={true}
        onOk={onAddAssets}
        onCancel={() =>
          setState((prev) => ({ ...prev, addAssetModalVisible: false }))
        }
      />
    </Form>
  );
};

WorkOrderDetailsForm.propTypes = {
  childRef: PropTypes.func,
};

export default WorkOrderDetailsForm;
